import { useCampaignActivationModalStore } from '@forwardflow/store/modal/campaignActivationModal'
import { Button } from '@forwardflow/ui'
import { message, Modal, Skeleton } from 'antd'
import { useParams, useSearchParams } from 'react-router-dom'
import { useGetCampaignActivationSummary } from '@forwardflow/features/campaign'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import CampaignService from '@forwardflow/services/CampaignService'
import { usePhoneNumberUnavailableModalStore } from '@forwardflow/store/modal/phoneNumberUnavailableModal'
import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '@forwardflow/features/campaign'
import { useCampaignPhoneNumberCountStore } from '@forwardflow/store/campaignPhoneNumberCountStore'
import { PhoneNumberUnavailableModal } from '../PhoneNumberUnavailableModal'
import { TermsOfService } from './TermsOfService'
import { useCampaignLiveModalStore } from '@forwardflow/store/modal/campaignLiveModal'

export const CampaignActivationModal = () => {
   const { title } = useParams()
   const [searchParams] = useSearchParams()
   const campaignName = searchParams.get('campaignName')
   const { isCampaignActivationModalOpen, closeCampaignActivationModal } =
      useCampaignActivationModalStore()
   const { count } = useCampaignPhoneNumberCountStore()
   const [hasNoPhoneNumbers, setHasNoPhoneNumbers] = useState(count === 0)
   const { openCampaignLiveModal } = useCampaignLiveModalStore()
   const { setUnavailablePhoneNumbers, openPhoneNumberUnavailableModal } =
      usePhoneNumberUnavailableModalStore()
   const [isLoading, setIsLoading] = useState(false)
   const [isBadCampaign, setIsBadCampaign] = useState(count > 2)
   useEffect(() => {
      setHasNoPhoneNumbers(count === 0)
      setIsBadCampaign(count > 2)
   }, [count])

   const {
      data: activationSummary,
      mutate,
      isLoading: activationSummaryLoading,
   } = useGetCampaignActivationSummary(title as string)

   const { mutate: mutateActiveLeadSource } = useGetActiveLeadSource(
      title || ''
   )

   const { data: organizationCampaignDetails, mutate: mutateCampaign } =
      useGetActiveLeadSourceDetails(title as string)

   useEffect(() => {
      mutate()
   }, [isCampaignActivationModalOpen])

   const onActiveCampaign = async () => {
      try {
         setIsLoading(true)
         if (
            organizationCampaignDetails?.status !== 'draft' &&
            !isCampaignActivationModalOpen
         ) {
            return false
         } else {
            const response = await CampaignService.activateCampaign(
               title as string
            )
            if (response.hasFailedPurchase) {
               mutateCampaign()
               closeCampaignActivationModal()

               const phoneNumberArray: string[] = []
               response.purchaseResult.failedPurchases.map((purchase) => {
                  phoneNumberArray.push(purchase.phoneNumber)
               })
               setUnavailablePhoneNumbers(phoneNumberArray)
               openPhoneNumberUnavailableModal()
               return false
            }
            message.success('Campaign activated successfully')
            mutateCampaign()
            mutateActiveLeadSource()
            closeCampaignActivationModal()
            openCampaignLiveModal()
         }
      } catch (e) {
         message.error(
            'Something went wrong while activating your campaign. Please check your campaign details and try again.'
         )
      } finally {
         setIsLoading(false)
      }
   }

   return (
      <>
         <PhoneNumberUnavailableModal />
         <Modal
            className="p-0 ff-modal"
            open={isCampaignActivationModalOpen}
            onCancel={closeCampaignActivationModal}
            footer={null}
         >
            {hasNoPhoneNumbers ? (
               <>
                  <div className="px-7 pt-[30px] mb-4 text-white">
                     <p className="text-[22px] font-medium mb-4">
                        No Phone Numbers Added
                     </p>
                     <div className="text-[#818181] text-[14px] font-medium pt-4">
                        You need to add at least 1 phone number to activate this
                        campaign. Please add phone numbers to continue.
                     </div>
                  </div>
                  <section className="my-8 px-7">
                     <Button
                        className="w-full text-white !rounded-[4px]"
                        type="submit"
                        onClick={closeCampaignActivationModal}
                     >
                        Add Phone Numbers
                     </Button>
                  </section>
               </>
            ) : !isBadCampaign ? (
               <>
                  <div className="px-7 pt-[30px] mb-4 text-white">
                     <p className="text-[22px] font-medium mb-4">
                        Bad Campaign Strength
                     </p>
                     <div className="text-[#818181] text-[14px] font-medium pt-4">
                        You have just added {count} numbers for the campaign,
                        whereas the optimal number of phone numbers required for
                        this campaign is 3. This will result in bad campaign
                        performance
                     </div>
                  </div>
                  <section className="mt-8 px-7">
                     <div className="flex justify-between py-2 bg-primary/10 px-7">
                        <div>
                           <p className="mb-2">Current</p>
                           <p>{count} numbers</p>
                        </div>
                        <div>
                           <p className="mb-2">Required for best performance</p>
                           <p>3 numbers</p>
                        </div>
                     </div>
                     <div className="flex gap-2 pt-6 pb-5">
                        <Button
                           className="w-full text-white !rounded-[4px] !bg-transparent hover:!bg-white/10 !border-white/20"
                           type="submit"
                           onClick={() => setIsBadCampaign(true)}
                        >
                           Activate anyway
                        </Button>
                        <Button
                           className="w-full text-white !rounded-[4px]"
                           type="submit"
                           onClick={closeCampaignActivationModal}
                        >
                           Add more
                        </Button>
                     </div>
                  </section>
               </>
            ) : (
               <>
                  <div className="px-7 pt-[30px] mb-4 text-white">
                     <p className="text-[22px] font-medium mb-4">
                        Confirm Activation
                     </p>
                     <div className="text-[#818181] text-[14px] font-medium pt-4">
                        Any new leads on this lead source will be auto-nurtured
                        from now on. You can Pause Campaign from
                        <span className="text-white">
                           &nbsp; Home &gt; Campaign &gt; {campaignName}
                        </span>
                     </div>
                  </div>

                  <section className="mt-8 px-7">
                     <div className=" bg-primary/10">
                        <p className="text-[14px] p-6">
                           A moderate rate for new phone numbers will be added
                           to your Monthly Subscription Plan as below
                        </p>
                        {!activationSummaryLoading ? (
                           <>
                              <div className="flex items-center justify-between px-8 py-3 mb-1 font-medium bg-primary/20">
                                 <div className="flex flex-col gap-y-2">
                                    <p className="text-[12px]">
                                       Current Payment Cycle ({' '}
                                       {
                                          activationSummary?.currentPeriod
                                             ?.daysRemaining
                                       }{' '}
                                       days remaining)
                                    </p>
                                    <p className="text-[14px]">
                                       {dayjs(
                                          activationSummary?.currentPeriod
                                             ?.paymentDate
                                       ).format('D MMM YYYY')}
                                    </p>
                                 </div>
                                 <div className="text-[20px]">
                                    ${' '}
                                    {activationSummary?.currentPeriod?.amount.toFixed(
                                       2
                                    )}
                                 </div>
                              </div>
                              <div className="flex items-center justify-between px-8 py-3 font-medium bg-primary/20">
                                 <div className="flex flex-col gap-y-2">
                                    <p className="text-[12px]">
                                       Next Payment Cycle
                                    </p>
                                    <p className="text-[14px]">
                                       {dayjs(
                                          activationSummary?.nextPeriod
                                             ?.paymentDate
                                       ).format('D MMM YYYY')}
                                    </p>
                                 </div>
                                 <div className="text-[20px]">
                                    ${' '}
                                    {activationSummary?.nextPeriod?.amount.toFixed(
                                       2
                                    )}
                                 </div>
                              </div>
                              <TermsOfService />
                           </>
                        ) : (
                           <div className="px-5">
                              <Skeleton active />
                           </div>
                        )}
                     </div>
                     {/* <TermsOfService /> */}
                     <div className="flex gap-2 pt-6 pb-5">
                        <Button
                           className="w-full text-white !rounded-[4px] !bg-transparent hover:!bg-white/10 !border-white/20"
                           type="submit"
                           onClick={closeCampaignActivationModal}
                        >
                           Back
                        </Button>
                        <Button
                           onClick={onActiveCampaign}
                           isLoading={isLoading}
                           className="w-full text-white !rounded-[4px]"
                           type="submit"
                        >
                           Agree and Activate
                        </Button>
                     </div>
                  </section>
               </>
            )}
         </Modal>
      </>
   )
}
